import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import isNil from 'lodash-es/isNil';
import trim from 'lodash-es/trim';
import { SubscriptionComponent } from '@liebherr/old-sage-ui';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, withLatestFrom } from 'rxjs/operators';
import { AppState } from '../../../app.models';
import * as fromTenants from '../../../tenants';
import { UserDto, TenantDto } from '../../../api/core';

@Component({
    selector: 'liebherr-tenant-manager',
    templateUrl: 'tenant-manager.component.html',
    styleUrls: ['tenant-manager.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TenantManagerComponent extends SubscriptionComponent implements AfterViewInit, OnChanges {
    @Input() parentControl?: UntypedFormControl;
    @Input() user?: UserDto;

    @ViewChild('paginator') set paginator(el: MatPaginator) {
        if (!isNil(el) && isNil(this.tenantSource.paginator)) {
            this.tenantSource.paginator = el;
        }
    }

    tenantSource = new MatTableDataSource<TenantDto>([]);
    tenantColumns = ['name'];

    tenantFilterCtrl = this.fb.control(undefined, Validators.required);

    tenantFilter$ = new BehaviorSubject<string | null>(null);
    filteredTenants$: Observable<TenantDto[]>;
    loading$ = this.store.select(fromTenants.selectLoading);
    tenants$ = this.store.select(fromTenants.selectTenants);
    tenants: TenantDto[] = [];

    tenantWillBeCreated$ = new BehaviorSubject<boolean>(false);

    constructor(private store: Store<AppState>, private fb: UntypedFormBuilder) {
        super();
        this.filteredTenants$ = combineLatest([this.tenants$, this.tenantFilter$]).pipe(
            map(([tenants, tenantFilter]) => {
                return !isNil(tenantFilter) ? tenants?.filter((t) => t.name?.includes(tenantFilter)) : tenants;
            })
        );

        this.sub.add(this.tenants$.subscribe((t) => (this.tenants = t)));
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.user) {
            const match = this.tenants.find((t) => t.id === this.user?.tenantId);
            this.tenantFilterCtrl.patchValue(match?.name);
            this.parentControl?.patchValue(match);
        }
    }

    ngAfterViewInit() {
        this.sub.add(
            this.tenantFilterCtrl.valueChanges
                .pipe(distinctUntilChanged(isEqual), debounceTime(500), withLatestFrom(this.tenants$))
                .subscribe(([change, tenants]) => {
                    const f = !isEmpty(trim(change)) ? trim(change) : null;

                    const match = tenants.find((t) => t.name === f);
                    const filtered = !isNil(f) ? tenants?.filter((t) => t.name?.includes(f)) : tenants;

                    if (match) {
                        this.parentControl?.patchValue(match);
                        this.parentControl?.markAsDirty();
                    } else {
                        const createNew = filtered.length === 0;
                        if (createNew) {
                            this.parentControl?.patchValue({ id: undefined, name: f });
                            this.parentControl?.markAsDirty();
                        }
                        this.tenantWillBeCreated$.next(createNew);
                    }

                    this.tenantFilter$.next(f);
                })
        );

        this.sub.add(
            this.filteredTenants$.subscribe((tenants) => {
                this.tenantSource.data = tenants;
            })
        );
    }

    onFocus(trigger?: MatMenuTrigger) {
        if (trigger) {
            trigger.openMenu();
        }
    }

    onSelection(tenant: TenantDto, trigger?: MatMenuTrigger) {
        this.tenantFilterCtrl.patchValue(tenant.name);
        this.tenantFilterCtrl.markAsDirty();
        if (trigger) {
            trigger.closeMenu();
        }
    }
}
